import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listPublishedProducts } from '@/services/products';
import { listCategories, listProductTypes } from '@/services/catalog';
import type { Product, ProductCategory, ProductTypeOption } from '@/domain/types';

const sortOptions = [
  { value: 'newest', label: 'Newest' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'name', label: 'Name A–Z' },
];

const typeColors: Record<string, string> = {
  plugin: 'bg-brand-50 text-brand-700', theme: 'bg-amber-50 text-amber-700',
  template: 'bg-sky-50 text-sky-700', addon: 'bg-violet-50 text-violet-700',
  asset: 'bg-rose-50 text-rose-700', app: 'bg-teal-50 text-teal-700',
};

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-surface-200 animate-pulse">
      <div className="aspect-[16/10] bg-surface-200" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-surface-200 rounded w-3/4" />
        <div className="h-3 bg-surface-200 rounded w-full" />
        <div className="h-3 bg-surface-200 rounded w-2/3" />
        <div className="flex justify-between pt-2"><div className="h-5 bg-surface-200 rounded w-16" /><div className="h-4 bg-surface-200 rounded w-20" /></div>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [types, setTypes] = useState<ProductTypeOption[]>([]);
  const [loading, setLoading] = useState(true);

  const activeType = searchParams.get('type') || '';
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    Promise.all([listPublishedProducts(), listCategories(), listProductTypes()])
      .then(([prods, cats, typeOpts]) => { setProducts(prods); setCategories(cats); setTypes(typeOpts); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let result = [...products];
    if (activeType) result = result.filter((p) => p.type === activeType || (p.typeNames ?? []).some((t) => t.toLowerCase() === activeType));
    if (activeCategory) result = result.filter((p) => p.categoryId === activeCategory);
    if (search.trim()) { const q = search.toLowerCase(); result = result.filter((p) => p.name.toLowerCase().includes(q) || p.shortDescription.toLowerCase().includes(q)); }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.priceFrom - b.priceFrom); break;
      case 'price-high': result.sort((a, b) => b.priceFrom - a.priceFrom); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return result;
  }, [products, activeType, activeCategory, search, sortBy]);

  const setParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    setSearchParams(params);
  };

  const clearFilters = () => setSearchParams({});
  const hasFilters = !!(activeType || activeCategory || search);

  return (
    <div className="min-h-screen bg-surface-50 pt-20 md:pt-24 pb-16">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-surface-900 mb-2">Browse Products</h1>
          <p className="text-surface-500 text-sm md:text-base">{loading ? 'Loading…' : `${filtered.length} product${filtered.length !== 1 ? 's' : ''} available`}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="flex items-center justify-between lg:hidden mb-3">
              <span className="text-sm font-medium text-surface-700">Filters</span>
              <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)} className="px-3 py-1.5 rounded-lg border border-surface-300 text-sm text-surface-600 flex items-center gap-1"><i className="ri-filter-3-line" /> {mobileFiltersOpen ? 'Hide' : 'Show'}</button>
            </div>
            <div className={`${mobileFiltersOpen ? 'block' : 'hidden'} lg:block space-y-4`}>
              <div className="bg-white rounded-xl border border-surface-200 p-4">
                <h3 className="text-sm font-semibold text-surface-900 mb-3">Product Type</h3>
                <div className="space-y-1">
                  <button onClick={() => setParam('type', '')} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeType ? 'bg-brand-50 text-brand-700 font-medium' : 'text-surface-600 hover:bg-surface-50'}`}>All Types</button>
                  {types.map((t) => {
                    const count = products.filter((p) => p.type === t.slug || (p.typeNames ?? []).some((n) => n.toLowerCase() === t.slug)).length;
                    return <button key={t.id} onClick={() => setParam('type', t.slug)} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${activeType === t.slug ? 'bg-brand-50 text-brand-700 font-medium' : 'text-surface-600 hover:bg-surface-50'}`}><span className="capitalize">{t.name}</span><span className="text-xs text-surface-400">{count}</span></button>;
                  })}
                </div>
              </div>
              {categories.length > 0 && (
                <div className="bg-white rounded-xl border border-surface-200 p-4">
                  <h3 className="text-sm font-semibold text-surface-900 mb-3">Category</h3>
                  <div className="space-y-1">
                    <button onClick={() => setParam('category', '')} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeCategory ? 'bg-brand-50 text-brand-700 font-medium' : 'text-surface-600 hover:bg-surface-50'}`}>All Categories</button>
                    {categories.filter((c) => c.active).map((c) => {
                      const count = products.filter((p) => p.categoryId === c.id).length;
                      return <button key={c.id} onClick={() => setParam('category', c.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${activeCategory === c.id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-surface-600 hover:bg-surface-50'}`}><span>{c.name}</span><span className="text-xs text-surface-400">{count}</span></button>;
                    })}
                  </div>
                </div>
              )}
              {hasFilters && <button onClick={clearFilters} className="w-full text-sm text-surface-500 hover:text-brand-600 py-2 transition-colors"><i className="ri-refresh-line mr-1" /> Clear all filters</button>}
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                <input type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white" />
              </div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2.5 rounded-lg border border-surface-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {(activeType || activeCategory) && (
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {activeType && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">Type: {activeType}<button onClick={() => setParam('type', '')} className="hover:text-brand-900"><i className="ri-close-line" /></button></span>}
                {activeCategory && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">{categories.find((c) => c.id === activeCategory)?.name || 'Category'}<button onClick={() => setParam('category', '')} className="hover:text-brand-900"><i className="ri-close-line" /></button></span>}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">{Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-surface-200">
                <i className="ri-search-line text-4xl text-surface-300 mb-3" />
                <p className="text-surface-500 font-medium">No products found</p>
                <p className="text-sm text-surface-400 mt-1">Try adjusting your filters or search query.</p>
                {hasFilters && <button onClick={clearFilters} className="mt-4 text-sm text-brand-600 hover:underline">Clear filters</button>}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((product) => (
                  <div key={product.id} onClick={() => navigate(`/product/${product.slug}`)} className="group bg-white rounded-xl overflow-hidden border border-surface-200 hover:border-brand-300 hover:shadow-lg transition-all duration-300 cursor-pointer">
                    <div className="relative overflow-hidden aspect-[16/10]">
                      {product.thumbnail ? <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /> : <div className="w-full h-full bg-gradient-to-br from-brand-50 to-surface-100 flex items-center justify-center"><i className="ri-box-3-line text-4xl text-brand-300" /></div>}
                      <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${typeColors[product.type] || 'bg-surface-100 text-surface-600'}`}>{product.type}</span>
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="font-semibold text-surface-900 text-base mb-1 group-hover:text-brand-600 transition-colors">{product.name}</h3>
                      <p className="text-sm text-surface-500 leading-relaxed mb-3 line-clamp-2">{product.shortDescription}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          {product.isFree ? <span className="text-lg font-bold text-brand-600">Free</span> : <><span className="text-lg font-bold text-surface-900">${product.priceFrom}</span><span className="text-xs text-surface-400">from</span></>}
                        </div>
                        <span className="text-sm font-medium text-brand-600 group-hover:text-brand-700 flex items-center gap-1">View <i className="ri-arrow-right-line" /></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
